/**
 * IFC Composition & Property Reality panel — Mar 2, 2026.
 *
 * Collapsible panel that sits above "Failures by IFC class" on the AIM
 * Register dashboard. Two tables:
 *
 *   Table A — Element population by IFC class (sorted by count desc).
 *             Sums reconcile to the audit's total_elements badge.
 *
 *   Table B — Watched-property presence. Split into two sections:
 *             1) Rule-driven properties (every active confirmed_rules
 *                ``field_to_check``).
 *             2) Default envelope set (ThermalTransmittance, FireRating,
 *                LoadBearing, IsExternal, AcousticRating).
 *             Coverage % denominator = applicable scope (from rule's
 *             target_ifc_classes OR the default envelope class set), NEVER
 *             all elements in the file.
 *
 * Data source priority (the hybrid persist + recompute strategy):
 *   1) ``audit.summary.composition`` (new audits; instant).
 *   2) ``GET /api/audits/{audit_id}/composition`` (historical audits;
 *      lazy-loaded the first time the panel is opened; writes-through
 *      so the next load hits path 1).
 *
 * Props:
 *   • ``audit``   — the audit object as fetched on ResultsPage.
 *   • ``auditId`` — the audit id (for the on-demand endpoint).
 */
import React, { useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../lib/supabase';
import { ChevronDown, ChevronRight, Loader2, Layers, FileSearch } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ────────────────────────────────────────────────────────

function _coverageColor(coverage) {
  if (coverage === null || coverage === undefined) return '#9ca3af';
  if (coverage >= 90) return '#166534';
  if (coverage >= 50) return '#854d0e';
  return '#991b1b';
}

function _coverageBg(coverage) {
  if (coverage === null || coverage === undefined) return '#f3f4f6';
  if (coverage >= 90) return '#dcfce7';
  if (coverage >= 50) return '#fef9c3';
  return '#fee2e2';
}

function _fmtCoverage(coverage) {
  if (coverage === null || coverage === undefined) return '—';
  return `${coverage.toFixed(1)}%`;
}

// ── Sub-tables ─────────────────────────────────────────────────────

function ElementPopulationTable({ rows, totalElements }) {
  if (!rows || rows.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        No element population data available.
      </p>
    );
  }
  const sumListed = rows.reduce((s, r) => s + (r.count || 0), 0);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#111', zIndex: 1 }}>
          <tr>
            <th style={{ textAlign: 'left',  padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>IFC Class</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>Count</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>% of total</th>
            <th style={{ textAlign: 'left',  padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.ifc_class}
              data-testid={`composition-pop-row-${i}`}
              style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.03)' }}
            >
              <td style={{ padding: '6px 8px', fontWeight: 600, color: '#111', fontVariantNumeric: 'tabular-nums' }}>{r.ifc_class}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#111', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.count.toLocaleString()}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151', fontVariantNumeric: 'tabular-nums' }}>{r.percent.toFixed(1)}%</td>
              <td style={{ padding: '6px 8px', color: '#6b7280', fontSize: 11 }}>{r.note || '—'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #e5e7eb' }}>
            <td style={{ padding: '6px 8px', fontWeight: 700, color: '#111' }}>Total ({rows.length} classes)</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#111', fontVariantNumeric: 'tabular-nums' }}>{sumListed.toLocaleString()}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>{totalElements ? Math.round(100 * sumListed / totalElements) : 100}%</td>
            <td style={{ padding: '6px 8px', color: '#6b7280', fontSize: 11 }}>
              {totalElements && sumListed !== totalElements
                ? `Reconciles to total ${totalElements.toLocaleString()} when more rows are shown`
                : 'Reconciles to audit total'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function PropertyPresenceSection({ title, hint, rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div>
        <h4 className="text-xs font-semibold text-foreground mb-1">{title}</h4>
        <p className="text-xs text-muted-foreground italic">No properties in this group.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div>
        <h4 className="text-xs font-semibold text-foreground">{title}</h4>
        {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#111', zIndex: 1 }}>
            <tr>
              <th style={{ textAlign: 'left',  padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>Property</th>
              <th style={{ textAlign: 'left',  padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>Present on (IFC classes)</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>Count present</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>Applicable scope</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111' }}>Coverage</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const cov = r.coverage_percent;
              return (
                <tr key={`${title}-${r.property}`}
                  data-testid={`composition-presence-${r.property}`}
                  style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.03)' }}
                >
                  <td style={{ padding: '6px 8px', fontWeight: 600, color: '#111' }}>{r.property}</td>
                  <td style={{ padding: '6px 8px', color: '#374151', fontSize: 11 }}>
                    {r.present_on?.length > 0
                      ? r.present_on.join(', ')
                      : (r.scope_classes?.length > 0
                          ? <span style={{ color: '#9ca3af' }}>(none of {r.scope_classes.join(', ')})</span>
                          : <span style={{ color: '#9ca3af' }}>(no scope)</span>
                        )}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#111', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {(r.count_present || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151', fontVariantNumeric: 'tabular-nums' }}>
                    {r.scope_total ? r.scope_total.toLocaleString() : '—'}
                  </td>
                  <td
                    style={{
                      padding: '6px 8px',
                      textAlign: 'right',
                      backgroundColor: _coverageBg(cov),
                      color: _coverageColor(cov),
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {_fmtCoverage(cov)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────

export default function IFCCompositionPanel({ audit, auditId }) {
  // Fast path: read the parser-persisted snapshot from
  // ``audit.summary`` at mount time. Using a useState initializer
  // (rather than a useEffect) so the value is set on the first render
  // — avoids the ``react-hooks/set-state-in-effect`` lint rule and
  // a one-frame flash where ``data`` is null.
  const initialData = (() => {
    const comp = audit?.summary?.composition;
    const tool = audit?.summary?.authoring_tool;
    if (comp && comp.element_population) {
      return {
        available:      true,
        source:         'cached',
        authoring_tool: tool || {},
        composition:    comp,
      };
    }
    return null;
  })();

  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [data,    setData]    = useState(initialData);
  const [error,   setError]   = useState(null);

  // Slow path: lazy-load from the endpoint when the panel opens and we
  // don't already have a cached snapshot. The endpoint writes-through
  // so subsequent reads on this audit hit the fast path.
  const loadOnDemand = useCallback(async () => {
    if (data?.available || loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/audits/${auditId}/composition`);
      if (!r.ok) {
        setError(`HTTP ${r.status}`);
        return;
      }
      const j = await r.json();
      setData(j);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [auditId, data, loading]);

  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !initialData && !data) {
      loadOnDemand();
    }
  };

  const composition = data?.composition;
  const totalElems  = composition?.total_elements
    || audit?.total_elements
    || 0;

  return (
    <div className="card-elevated p-4" data-testid="ifc-composition-panel">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="ifc-composition-toggle"
      >
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-[hsl(var(--accent-foreground))]" />
          <h3 className="text-sm font-semibold text-foreground">
            IFC composition &amp; property reality
          </h3>
          {data?.source && (
            <span className="text-[10px] text-muted-foreground italic">
              {data.source === 'cached' ? 'snapshot at audit time'
                : data.source === 'recomputed_verified' ? 'recomputed · hash verified'
                : data.source === 'recomputed_unverified' ? 'recomputed · no hash on record'
                : ''}
            </span>
          )}
        </div>
        {open
          ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>
      <p className="text-xs text-muted-foreground mt-1">
        Distinguish a real data-quality gap from an applicability problem.
        This panel's coverage % is computed against the applicable scope, never the full
        file — the property-coverage table above uses the full file instead.
      </p>

      {open && (
        <div className="mt-4 space-y-5" data-testid="ifc-composition-body">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Recomputing composition from the IFC… this can take a few seconds.
            </div>
          )}

          {error && (
            <div className="text-xs text-[hsl(var(--destructive))]">
              Composition unavailable: {error}
            </div>
          )}

          {!loading && data && !data.available && (
            <div className="text-xs text-muted-foreground italic">
              <FileSearch className="inline h-3 w-3 mr-1" />
              {data.reason === 'ifc_path_missing' || data.reason === 'ifc_path_missing_pre_feature'
                ? 'Composition unavailable: the source IFC file is no longer on disk (pre-feature audit).'
                : data.reason === 'ifc_hash_mismatch'
                ? 'Composition unavailable: the on-disk IFC no longer matches the hash recorded at audit time. Re-run the audit to refresh.'
                : `Composition unavailable: ${data.reason || 'unknown'}.`}
            </div>
          )}

          {composition && composition.element_population && (
            <>
              {/* Table A — Element population by IFC class */}
              <div>
                <h4 className="text-xs font-semibold text-foreground mb-1">
                  Table A · Element population by IFC class
                </h4>
                <p className="text-[10px] text-muted-foreground mb-2">
                  Groups all <code>IfcElement</code> instances by leaf class. Totals reconcile to the audit&apos;s
                  {' '}{totalElems.toLocaleString()} total elements.
                </p>
                <ElementPopulationTable
                  rows={composition.element_population}
                  totalElements={totalElems}
                />
              </div>

              {/* Table B — Watched property presence (split) */}
              <PropertyPresenceSection
                title="Table B · Rule-driven properties"
                hint="Properties referenced by active confirmed_rules.field_to_check — scoped to each rule's target IFC classes."
                rows={composition.property_presence?.rule_driven || []}
              />
              <PropertyPresenceSection
                title="Default envelope set (context)"
                hint="Standard ISO 19650 envelope checks — scoped to the default envelope class set (IfcWall, IfcDoor, IfcWindow, IfcSlab, IfcRoof, IfcCovering, IfcCurtainWall)."
                rows={composition.property_presence?.envelope || []}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
